/**
 * 本店人脸库管理（一期不加 PermissionA5）
 * 列表 / 登记（拍照或上传）/ 删除
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Drawer,
  Empty,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Upload,
  message
} from 'antd'
import {
  CameraOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { debounce } from 'lodash'
import {
  FACE_FAIL_REASON_TEXT,
  deleteFaceUser,
  listFaceUsers,
  registerFace
} from '@/service/face'
import { getSystemAgentsList } from '@/service/systemControl'
import { renderTextStr, renderPhoneStr } from '@/utils/renderUtils'
import { acquireFaceStream } from './faceCaptureSession'

/** GET /system/agents/page — 在职 + 分页 + keyword */
const FACE_AGENT_LIST_QUERY = {
  pageNumber: 1,
  pageSize: 20,
  orderBy: 'create_time',
  orderDirection: 'desc',
  state: 1
}

function normalizeAgentRecords (body) {
  if (!body || typeof body !== 'object') return []
  if (Array.isArray(body.list)) return body.list
  if (Array.isArray(body.records)) return body.records
  if (Array.isArray(body.data?.list)) return body.data.list
  if (Array.isArray(body.data?.records)) return body.data.records
  return []
}

function agentToFaceOption (a) {
  if (!a || a.id == null) return null
  const name = renderTextStr(a.userName)
  if (!name) return null
  const phone = a.phone
  return {
    value: String(a.id),
    label: phone ? `${name} · ${renderPhoneStr(phone)}` : name,
    name
  }
}

function cameraErrorText (err) {
  const name = err && err.name
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return '摄像头权限被拒绝，请在浏览器地址栏允许摄像头后重试'
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return '未检测到摄像头设备'
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return '摄像头被占用，请关闭其他占用摄像头的页面后重试'
  }
  if (name === 'SecurityError') {
    return '当前页面非安全环境，请用 https 或 localhost 打开'
  }
  if (name === 'NotSupportedError') {
    return '当前浏览器不支持摄像头调用'
  }
  return '无法打开摄像头，请检查权限或改用上传'
}

function formatEnrolledAt (value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function stopMediaStream (stream) {
  if (!stream) return
  stream.getTracks().forEach((t) => {
    try {
      t.stop()
    } catch (e) {
      /* ignore */
    }
  })
}

function StoreFaceLibraryDrawer ({ open, onClose, storeId }) {
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [agentId, setAgentId] = useState(undefined)
  const [agentName, setAgentName] = useState('')
  const [agentOptions, setAgentOptions] = useState([])
  const [agentLoading, setAgentLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [blob, setBlob] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [cameraOn, setCameraOn] = useState(false)

  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const canvasRef = useRef(null)
  const agentSeqRef = useRef(0)

  const storeMissing = storeId === undefined || storeId === null || storeId === ''

  const clearPreview = useCallback(() => {
    setBlob(null)
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return ''
    })
  }, [])

  const stopCamera = useCallback(() => {
    stopMediaStream(streamRef.current)
    streamRef.current = null
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setCameraOn(false)
  }, [])

  const clearAgent = useCallback(() => {
    setAgentId(undefined)
    setAgentName('')
  }, [])

  const loadAgents = useCallback(
    async (keywordRaw) => {
      if (storeMissing) {
        setAgentOptions([])
        return
      }
      const kw = typeof keywordRaw === 'string' ? keywordRaw.trim() : ''
      const seq = ++agentSeqRef.current
      setAgentLoading(true)
      try {
        const res = await getSystemAgentsList({
          ...FACE_AGENT_LIST_QUERY,
          storeId,
          ...(kw !== '' ? { keyword: kw } : {})
        })
        if (seq !== agentSeqRef.current) return
        const next = normalizeAgentRecords(res?.data ?? {})
          .map((a) => agentToFaceOption(a))
          .filter(Boolean)
        setAgentOptions(next)
      } catch (e) {
        if (seq === agentSeqRef.current) {
          setAgentOptions([])
        }
      } finally {
        if (seq === agentSeqRef.current) {
          setAgentLoading(false)
        }
      }
    },
    [storeId, storeMissing]
  )

  const debouncedLoadAgents = useMemo(() => debounce((k) => loadAgents(k), 300), [loadAgents])

  const loadUsers = useCallback(async () => {
    if (storeMissing) {
      setUsers([])
      return
    }
    setLoading(true)
    try {
      const res = await listFaceUsers({ storeId })
      if (!res.ok) {
        message.error(FACE_FAIL_REASON_TEXT[res.reason] || '加载名单失败')
        setUsers([])
        return
      }
      setUsers(res.users)
    } finally {
      setLoading(false)
    }
  }, [storeId, storeMissing])

  useEffect(() => {
    if (!open) {
      stopCamera()
      clearPreview()
      clearAgent()
      setAgentOptions([])
      setUsers([])
      return undefined
    }
    clearAgent()
    loadUsers()
    loadAgents('')
    return () => {
      stopCamera()
      debouncedLoadAgents.cancel()
    }
  }, [open, loadUsers, loadAgents, debouncedLoadAgents, stopCamera, clearPreview, clearAgent])

  useEffect(() => () => debouncedLoadAgents.cancel(), [debouncedLoadAgents])

  // video 仅在 cameraOn 后挂载，须等 DOM 就绪再绑流
  useEffect(() => {
    if (!cameraOn || !streamRef.current || !videoRef.current) return undefined
    const video = videoRef.current
    video.srcObject = streamRef.current
    const playPromise = video.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {})
    }
    return undefined
  }, [cameraOn])

  const startCamera = async () => {
    clearPreview()
    stopCamera()
    try {
      const stream = await acquireFaceStream()
      streamRef.current = stream
      setCameraOn(true)
    } catch (err) {
      message.error(cameraErrorText(err))
      setCameraOn(false)
    }
  }

  const capturePhoto = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) {
      message.warning('请稍候，摄像头尚未就绪')
      return
    }
    const canvas = canvasRef.current || document.createElement('canvas')
    canvasRef.current = canvas
    const maxSide = 720
    const scale = Math.min(1, maxSide / Math.max(video.videoWidth, video.videoHeight))
    const w = Math.max(2, Math.floor(video.videoWidth * scale))
    const h = Math.max(2, Math.floor(video.videoHeight * scale))
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0, w, h)
    canvas.toBlob(
      (nextBlob) => {
        if (!nextBlob) {
          message.error('抓拍失败')
          return
        }
        setBlob(nextBlob)
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev)
          return URL.createObjectURL(nextBlob)
        })
        stopCamera()
      },
      'image/jpeg',
      0.9
    )
  }

  const handleUpload = (file) => {
    stopCamera()
    clearPreview()
    setBlob(file)
    setPreviewUrl(URL.createObjectURL(file))
    return false
  }

  const handleAgentChange = (value, opt) => {
    if (value === undefined || value === null || value === '') {
      clearAgent()
      return
    }
    setAgentId(String(value))
    const fromOpt = opt && typeof opt === 'object' ? String(opt.name || '').trim() : ''
    if (fromOpt) {
      setAgentName(fromOpt)
      return
    }
    const hit = agentOptions.find((o) => o.value === String(value))
    setAgentName(hit ? hit.name : '')
  }

  const handleRegister = async () => {
    const trimmed = String(agentName || '').trim()
    if (!agentId || !trimmed) {
      message.warning(storeMissing ? '请先选择门店' : '请选择在职员工')
      return
    }
    if (!blob) {
      message.warning('请先拍照或上传露脸照（正对、清晰、不戴口罩）')
      return
    }
    setSubmitting(true)
    try {
      // person_id = agents.id，刷脸 nurseId 同值，供去向登记操作人
      const res = await registerFace({
        name: trimmed,
        blob,
        storeId,
        personId: agentId
      })
      if (!res.ok) {
        message.error(FACE_FAIL_REASON_TEXT[res.reason] || '登记失败')
        return
      }
      message.success(`已登记：${res.name}`)
      clearAgent()
      clearPreview()
      await loadUsers()
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (personId) => {
    if (storeMissing) {
      message.warning('请先选择门店')
      return
    }
    const res = await deleteFaceUser({ personId, storeId })
    if (!res.ok) {
      message.error(FACE_FAIL_REASON_TEXT[res.reason] || '删除失败')
      return
    }
    message.success('已删除')
    loadUsers()
  }

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true
    },
    {
      title: '登记时间',
      dataIndex: 'enrolled_at',
      key: 'enrolled_at',
      width: 140,
      render: formatEnrolledAt
    },
    {
      title: '操作',
      key: 'action',
      width: 72,
      render: (_, row) => (
        <Popconfirm
          title={`确定删除「${row.name}」？删除后无法再刷脸命中`}
          okText="删除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
          onConfirm={() => handleDelete(row.person_id)}
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      )
    }
  ]

  return (
    <Drawer
      title="本店人脸库"
      width={480}
      open={open}
      onClose={onClose}
      destroyOnClose
      className="store-face-library-drawer"
    >
      <div className="store-face-library">
        <section className="store-face-library__enroll">
          <div className="store-face-library__section-title">登记人员</div>
          <p className="store-face-library__hint">
            请露脸正对镜头、画面清晰（不戴口罩）。识别时全脸或戴口罩均可。勿用网图灌库。
          </p>
          <Select
            showSearch
            allowClear
            filterOption={false}
            placeholder={storeMissing ? '请先选择门店' : '搜索姓名/手机号/账号'}
            disabled={storeMissing}
            loading={agentLoading}
            notFoundContent={agentLoading ? <Spin size="small" /> : null}
            options={agentOptions}
            value={agentId}
            onChange={handleAgentChange}
            onSearch={(s) => {
              setAgentLoading(true)
              debouncedLoadAgents(s || '')
            }}
            onDropdownVisibleChange={(visible) => {
              if (visible && !storeMissing) {
                loadAgents('')
              }
            }}
            style={{ width: '100%' }}
          />
          <div className="store-face-library__preview">
            {cameraOn ? (
              <video
                ref={videoRef}
                className="store-face-library__video"
                muted
                playsInline
                autoPlay
              />
            ) : previewUrl ? (
              <img src={previewUrl} alt="登记预览" className="store-face-library__img" />
            ) : (
              <div className="store-face-library__placeholder">拍照或上传露脸照</div>
            )}
          </div>
          <Space wrap className="store-face-library__actions">
            {!cameraOn ? (
              <Button icon={<CameraOutlined />} onClick={startCamera}>
                打开摄像头
              </Button>
            ) : (
              <>
                <Button type="primary" onClick={capturePhoto}>
                  拍照
                </Button>
                <Button onClick={stopCamera}>关闭摄像头</Button>
              </>
            )}
            <Upload
              accept="image/jpeg,image/png,image/webp"
              showUploadList={false}
              beforeUpload={handleUpload}
            >
              <Button icon={<UploadOutlined />}>上传照片</Button>
            </Upload>
            {(previewUrl || blob) && !cameraOn ? (
              <Button onClick={clearPreview}>清除照片</Button>
            ) : null}
          </Space>
          <div className="store-face-library__submit">
            <Button type="primary" block loading={submitting} onClick={handleRegister}>
              登记到本店库
            </Button>
          </div>
        </section>

        <section className="store-face-library__list">
          <div className="store-face-library__list-head">
            <span className="store-face-library__section-title">已登记（{users.length}）</span>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={loadUsers}
              loading={loading}
            >
              刷新
            </Button>
          </div>
          <Table
            size="small"
            rowKey="person_id"
            loading={loading}
            columns={columns}
            dataSource={users}
            pagination={false}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无人脸" />
            }}
          />
        </section>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Drawer>
  )
}

StoreFaceLibraryDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  storeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

StoreFaceLibraryDrawer.defaultProps = {
  open: false,
  onClose: () => {},
  storeId: undefined
}

export default StoreFaceLibraryDrawer
